import type { ColumnConfig, ColumnGroup } from '../types';

export interface GroupedColumnStructure {
  groupHeaders: Array<{
    id: string;
    name: string;
    startIndex: number;
    endIndex: number;
    cssClass?: string;
    alignment?: 'left' | 'center' | 'right';
    width?: string;
    isDefault?: boolean;
  }>;
  columnHeaders: Array<{
    columnName: string;
    field: string;
    groupId?: string;
    columnConfig: ColumnConfig;
  }>;
}

/**
 * Creates a grouped column structure from columns and groups
 */
export function createGroupedColumnStructure(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroup[]
): GroupedColumnStructure {
  const groupHeaders: GroupedColumnStructure['groupHeaders'] = [];
  const columnHeaders: GroupedColumnStructure['columnHeaders'] = [];
  
  if (!columnGroups || columnGroups.length === 0) {
    // No groups, return flat structure
    columns.forEach((column) => {
      columnHeaders.push({
        columnName: column.columnName,
        field: column.field || column.columnName,
        columnConfig: column
      });
    });
    
    return { groupHeaders, columnHeaders };
  }

  // Sort groups by order
  const sortedGroups = [...columnGroups].sort((a, b) => a.order - b.order);
  
  let currentIndex = 0;
  
  // Process grouped columns
  sortedGroups.forEach(group => {
    if (!group.visible) return;
    
    const groupColumns = columns.filter(col => 
      group.columns.includes(col.field || col.columnName)
    );
    
    if (groupColumns.length > 0) {
      const startIndex = currentIndex;
      const endIndex = currentIndex + groupColumns.length - 1;
      
      groupHeaders.push({
        id: group.id,
        name: group.name,
        startIndex,
        endIndex,
        cssClass: group.cssClass,
        alignment: group.alignment,
        width: group.width,
        isDefault: group.isDefault
      });
      
      groupColumns.forEach(column => {
        columnHeaders.push({
          columnName: column.columnName,
          field: column.field || column.columnName,
          groupId: group.id,
          columnConfig: column
        });
        currentIndex++;
      });
    }
  });
  
  // Process ungrouped columns
  const groupedColumnFields = new Set(
    sortedGroups.flatMap(group => group.columns)
  );
  
  const ungroupedColumns = columns.filter(col => 
    !groupedColumnFields.has(col.field || col.columnName)
  );
  
  ungroupedColumns.forEach(column => {
    columnHeaders.push({
      columnName: column.columnName,
      field: column.field || column.columnName,
      columnConfig: column
    });
    currentIndex++;
  });
  
  return { groupHeaders, columnHeaders };
}

/**
 * Gets the column span for a group header
 */
export function getGroupColumnSpan(
  groupId: string,
  columnHeaders: GroupedColumnStructure['columnHeaders']
): number {
  return columnHeaders.filter(header => header.groupId === groupId).length;
}

/**
 * Checks if a column is part of a group
 */
export function isColumnInGroup(
  columnField: string,
  columnGroups?: ColumnGroup[]
): string | null {
  if (!columnGroups) return null;
  
  for (const group of columnGroups) {
    if (group.columns.includes(columnField)) {
      return group.id;
    }
  }
  
  return null;
}

/**
 * Gets the group configuration for a column
 */
export function getColumnGroup(
  columnField: string,
  columnGroups?: ColumnGroup[]
): ColumnGroup | null {
  if (!columnGroups) return null;
  
  return columnGroups.find(group => group.columns.includes(columnField)) || null;
}

/**
 * Creates a new column group
 */
export function createColumnGroup(
  name: string,
  columns: string[] = [],
  order: number = 0
): ColumnGroup {
  return {
    id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    columns,
    order,
    visible: true,
    isDefault: false,
    alignment: 'center'
  };
}

/**
 * Validates column group configuration
 */
export function validateColumnGroups(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroup[]
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!columnGroups || columnGroups.length === 0) {
    return { isValid: true, errors: [] };
  }
  
  const allColumnFields = new Set(
    columns.map(col => col.field || col.columnName)
  );
  
  columnGroups.forEach((group, index) => {
    if (!group.id) {
      errors.push(`Group ${index + 1}: id is required`);
    }
    
    if (!group.name) {
      errors.push(`Group ${index + 1}: name is required`);
    }
    
    if (!group.columns || group.columns.length === 0) {
      errors.push(`Group ${index + 1}: columns array is required and cannot be empty`);
    }
    
    group.columns.forEach(columnField => {
      if (!allColumnFields.has(columnField)) {
        errors.push(`Group ${index + 1}: Column '${columnField}' does not exist in the columns array`);
      }
    });
  });
  
  // Check for duplicate column assignments
  const columnAssignments = new Map<string, string[]>();
  columnGroups.forEach(group => {
    group.columns.forEach(columnField => {
      if (!columnAssignments.has(columnField)) {
        columnAssignments.set(columnField, []);
      }
      columnAssignments.get(columnField)!.push(group.id);
    });
  });
  
  columnAssignments.forEach((groups, columnField) => {
    if (groups.length > 1) {
      errors.push(`Column '${columnField}' is assigned to multiple groups: ${groups.join(', ')}`);
    }
  });
  
  // Check for duplicate group IDs
  const groupIds = new Set<string>();
  columnGroups.forEach(group => {
    if (groupIds.has(group.id)) {
      errors.push(`Duplicate group ID: ${group.id}`);
    }
    groupIds.add(group.id);
  });
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Assigns a column to a group
 */
export function assignColumnToGroup(
  columnField: string,
  groupId: string,
  columnGroups: ColumnGroup[]
): ColumnGroup[] {
  return columnGroups.map(group => {
    if (group.id === groupId) {
      // Add column to this group if not already present
      if (!group.columns.includes(columnField)) {
        return {
          ...group,
          columns: [...group.columns, columnField]
        };
      }
    } else {
      // Remove column from other groups
      return {
        ...group,
        columns: group.columns.filter(col => col !== columnField)
      };
    }
    return group;
  });
}

/**
 * Removes a column from all groups
 */
export function removeColumnFromGroups(
  columnField: string,
  columnGroups: ColumnGroup[]
): ColumnGroup[] {
  return columnGroups.map(group => ({
    ...group,
    columns: group.columns.filter(col => col !== columnField)
  }));
}

/**
 * Updates a group's properties
 */
export function updateColumnGroup(
  groupId: string,
  updates: Partial<ColumnGroup>,
  columnGroups: ColumnGroup[]
): ColumnGroup[] {
  return columnGroups.map(group => 
    group.id === groupId ? { ...group, ...updates } : group
  );
}

/**
 * Deletes a column group
 */
export function deleteColumnGroup(
  groupId: string,
  columnGroups: ColumnGroup[]
): ColumnGroup[] {
  return columnGroups.filter(group => group.id !== groupId);
}

/**
 * Reorders column groups
 */
export function reorderColumnGroups(
  groupIds: string[],
  columnGroups: ColumnGroup[]
): ColumnGroup[] {
  const groupMap = new Map(columnGroups.map(group => [group.id, group]));
  
  return groupIds.map((id, index) => {
    const group = groupMap.get(id);
    return group ? { ...group, order: index } : null;
  }).filter(Boolean) as ColumnGroup[];
}

/**
 * Gets available columns that can be assigned to groups
 */
export function getAvailableColumns(
  columns: ColumnConfig[],
  columnGroups: ColumnGroup[]
): ColumnConfig[] {
  const groupedColumnFields = new Set(
    columnGroups.flatMap(group => group.columns)
  );
  
  return columns.filter(col => 
    !groupedColumnFields.has(col.field || col.columnName)
  );
}

/**
 * Creates default column groups for common use cases
 */
export function createDefaultColumnGroups(): ColumnGroup[] {
  return [
    {
      id: 'naadu_group',
      name: 'NAADU',
      columns: ['naadu_long_view', 'naadu_short_view'],
      order: 1,
      cssClass: 'bg-violet-50 text-violet-900 font-semibold',
      alignment: 'center',
      visible: true,
      isDefault: true
    },
    {
      id: 'nedu_group',
      name: 'NEDU',
      columns: ['nedu_long_view', 'nedu_short_view'],
      order: 2,
      cssClass: 'bg-green-50 text-green-900 font-semibold',
      alignment: 'center',
      visible: true,
      isDefault: true
    }
  ];
}
